import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import type {
  AttendanceHistoryPage,
  AttendanceKioskLink,
  AttendanceLedgerEntry,
  AttendanceLedgerPage,
  AttendancePage,
  AttendanceRecord,
  AttendanceSettings,
  CheckInInput,
  CheckOutInput,
  CreateAttendanceLedgerEntryInput,
  CreateLeaveRequestInput,
  DecideLeaveRequestInput,
  LeaveRequest,
  LeaveRequestPage,
  RecordAttendanceInput,
  UpdateAttendanceSettingsInput,
} from '@tailoring-crm/shared';
import { useApiClient } from '../api/api-context.js';
import {
  askForLeave,
  cancelLeaveRequest,
  checkIn,
  checkOut,
  createLedgerEntry,
  decideLeaveRequest,
  deleteLedgerEntry,
  getAttendanceHistory,
  getAttendanceLedger,
  getLeaveRequests,
  getAttendanceKioskLink,
  getAttendanceSettings,
  recordAttendance,
  revokeAttendanceKioskLink,
  rotateAttendanceKioskLink,
  searchAttendance,
  updateAttendanceSettings,
  type AttendanceHistoryParams,
  type AttendanceSearchParams,
  type LeaveDecision,
  type LedgerParams,
  type LeaveRequestParams,
} from './attendance-api.js';

/**
 * The attendance hooks.
 *
 * None of these goes through the offline write queue, and the reason is the same one the shop
 * would give: a check-in is a claim about a MOMENT. Replaying "she arrived" from a queue forty
 * minutes after the connection came back would file an arrival at the wrong time and quietly
 * turn a punctual morning into a late one. A tap that fails is one somebody makes again, and the
 * manager's correction exists precisely for the arrival nobody managed to record.
 *
 * EVERY MUTATION INVALIDATES EVERYTHING under `attendanceKeys.all`, the settings mutation
 * included. That last one is not tidiness: grace and half-day minutes are what give the `late`
 * and `halfDay` flags their meaning, so widening the grace period changes rows that are already
 * on screen. Refreshing only the settings would leave the form saying "20 minutes" above a table
 * still marking somebody late at ten past.
 */

export const attendanceKeys = {
  all: ['attendance'] as const,
  search: (params: AttendanceSearchParams) => ['attendance', 'search', params] as const,
  settings: () => ['attendance', 'settings'] as const,
  history: (params: AttendanceHistoryParams) => ['attendance', 'history', params] as const,
  kioskLink: () => ['attendance', 'kiosk-link'] as const,
  ledger: (params: LedgerParams) => ['attendance', 'ledger', params] as const,
  leaveRequests: (params: LeaveRequestParams) => ['attendance', 'leave', params] as const,
};

/**
 * One day's roll call. `placeholderData` keeps the day already on screen while the next one
 * loads, so changing the date or the role filter does not blank the table under somebody's hand.
 *
 * `enabled` exists for the caller that must not ask at all — a screen that renders its refusal
 * while still firing a request that 403s teaches the API to log an error about nothing.
 */
export function useAttendanceSearch(
  params: AttendanceSearchParams,
  options: { enabled?: boolean } = {},
): UseQueryResult<AttendancePage> {
  const client = useApiClient();
  return useQuery({
    queryKey: attendanceKeys.search(params),
    queryFn: () => searchAttendance(client, params),
    enabled: options.enabled ?? true,
    placeholderData: (previous) => previous,
  });
}

/** One person over a range, for the history a wage conversation starts from. */
export function useAttendanceHistory(
  params: AttendanceHistoryParams,
  options: { enabled?: boolean } = {},
): UseQueryResult<AttendanceHistoryPage> {
  const client = useApiClient();
  return useQuery({
    queryKey: attendanceKeys.history(params),
    queryFn: () => getAttendanceHistory(client, params),
    enabled: options.enabled ?? true,
    placeholderData: (previous) => previous,
  });
}

export function useAttendanceSettings(
  options: { enabled?: boolean } = {},
): UseQueryResult<AttendanceSettings> {
  const client = useApiClient();
  return useQuery({
    queryKey: attendanceKeys.settings(),
    queryFn: () => getAttendanceSettings(client),
    enabled: options.enabled ?? true,
  });
}

/**
 * Invalidate what an attendance write can have changed — which is all of it.
 *
 * One helper, used by every mutation below including the settings one, so there is no way to add
 * a write that refreshes half the screen.
 */
function useAttendanceInvalidation(): () => void {
  const queryClient = useQueryClient();
  return () => {
    void queryClient.invalidateQueries({ queryKey: attendanceKeys.all });
  };
}

export function useCheckIn(): UseMutationResult<AttendanceRecord, unknown, CheckInInput> {
  const client = useApiClient();
  const invalidate = useAttendanceInvalidation();
  return useMutation({
    mutationFn: (input: CheckInInput) => checkIn(client, input),
    onSuccess: invalidate,
  });
}

export function useCheckOut(): UseMutationResult<AttendanceRecord, unknown, CheckOutInput> {
  const client = useApiClient();
  const invalidate = useAttendanceInvalidation();
  return useMutation({
    mutationFn: (input: CheckOutInput) => checkOut(client, input),
    onSuccess: invalidate,
  });
}

export function useRecordAttendance(): UseMutationResult<
  AttendanceRecord,
  unknown,
  RecordAttendanceInput
> {
  const client = useApiClient();
  const invalidate = useAttendanceInvalidation();
  return useMutation({
    mutationFn: (input: RecordAttendanceInput) => recordAttendance(client, input),
    onSuccess: invalidate,
  });
}

/**
 * A day taken as leave. The one write that is always a person's decision rather than a clock's,
 * which is why it carries a reason and goes through the correction endpoint rather than through
 * either of the two buttons.
 */
export function useMarkLeave(): UseMutationResult<
  AttendanceRecord,
  unknown,
  { staffUserId: string; day: string; reason?: string | null }
> {
  const client = useApiClient();
  const invalidate = useAttendanceInvalidation();
  return useMutation({
    mutationFn: ({
      staffUserId,
      day,
      reason,
    }: {
      staffUserId: string;
      day: string;
      reason?: string | null;
    }) =>
      recordAttendance(client, {
        staffUserId,
        day,
        status: 'on_leave',
        leaveReason: reason ?? null,
      }),
    onSuccess: invalidate,
  });
}

export function useUpdateAttendanceSettings(): UseMutationResult<
  AttendanceSettings,
  unknown,
  UpdateAttendanceSettingsInput
> {
  const client = useApiClient();
  const invalidate = useAttendanceInvalidation();
  return useMutation({
    mutationFn: (patch: UpdateAttendanceSettingsInput) => updateAttendanceSettings(client, patch),
    // The settings mutation invalidates the day too — see the note at the top of this file.
    onSuccess: invalidate,
  });
}

// --- The attendance kiosk link (task 18) -------------------------------------------------------

export function useAttendanceKioskLink(
  options: { enabled?: boolean } = {},
): UseQueryResult<AttendanceKioskLink> {
  const client = useApiClient();
  return useQuery({
    queryKey: attendanceKeys.kioskLink(),
    queryFn: () => getAttendanceKioskLink(client),
    enabled: options.enabled ?? true,
  });
}

function useKioskLinkInvalidation(): () => void {
  const queryClient = useQueryClient();
  return () => {
    void queryClient.invalidateQueries({ queryKey: attendanceKeys.kioskLink() });
  };
}

export function useRotateAttendanceKioskLink(): UseMutationResult<
  AttendanceKioskLink,
  unknown,
  void
> {
  const client = useApiClient();
  const invalidate = useKioskLinkInvalidation();
  return useMutation({
    mutationFn: () => rotateAttendanceKioskLink(client),
    onSuccess: invalidate,
  });
}

export function useRevokeAttendanceKioskLink(): UseMutationResult<void, unknown, void> {
  const client = useApiClient();
  const invalidate = useKioskLinkInvalidation();
  return useMutation({
    mutationFn: () => revokeAttendanceKioskLink(client),
    onSuccess: invalidate,
  });
}

// --- The HR ledger and leave requests (tier 2, item 9) -----------------------------------------

/**
 * Overtime and permission, and asking for days off.
 *
 * Every mutation here goes through the SAME `useAttendanceInvalidation` the clock buttons use,
 * and that is load-bearing rather than tidy: approving leave writes `on_leave` rows onto the
 * roster, so a decision that refreshed only the request list would leave the day list beside it
 * still showing everybody as expected in.
 */

export function useAttendanceLedger(
  params: LedgerParams,
  options: { enabled?: boolean } = {},
): UseQueryResult<AttendanceLedgerPage> {
  const client = useApiClient();
  return useQuery({
    queryKey: attendanceKeys.ledger(params),
    queryFn: () => getAttendanceLedger(client, params),
    enabled: options.enabled ?? true,
    placeholderData: (previous) => previous,
  });
}

export function useCreateLedgerEntry(): UseMutationResult<
  AttendanceLedgerEntry,
  unknown,
  CreateAttendanceLedgerEntryInput
> {
  const client = useApiClient();
  const invalidate = useAttendanceInvalidation();
  return useMutation({
    mutationFn: (input: CreateAttendanceLedgerEntryInput) => createLedgerEntry(client, input),
    onSuccess: invalidate,
  });
}

export function useDeleteLedgerEntry(): UseMutationResult<void, unknown, string> {
  const client = useApiClient();
  const invalidate = useAttendanceInvalidation();
  return useMutation({
    mutationFn: (entryId: string) => deleteLedgerEntry(client, entryId),
    onSuccess: invalidate,
  });
}

export function useLeaveRequests(
  params: LeaveRequestParams = {},
  options: { enabled?: boolean } = {},
): UseQueryResult<LeaveRequestPage> {
  const client = useApiClient();
  return useQuery({
    queryKey: attendanceKeys.leaveRequests(params),
    queryFn: () => getLeaveRequests(client, params),
    enabled: options.enabled ?? true,
    placeholderData: (previous) => previous,
  });
}

export function useAskForLeave(): UseMutationResult<
  LeaveRequest,
  unknown,
  CreateLeaveRequestInput
> {
  const client = useApiClient();
  const invalidate = useAttendanceInvalidation();
  return useMutation({
    mutationFn: (input: CreateLeaveRequestInput) => askForLeave(client, input),
    onSuccess: invalidate,
  });
}

export function useDecideLeaveRequest(): UseMutationResult<
  LeaveDecision,
  unknown,
  { requestId: string } & DecideLeaveRequestInput
> {
  const client = useApiClient();
  const invalidate = useAttendanceInvalidation();
  return useMutation({
    mutationFn: ({ requestId, ...input }: { requestId: string } & DecideLeaveRequestInput) =>
      decideLeaveRequest(client, requestId, input),
    onSuccess: invalidate,
  });
}

export function useCancelLeaveRequest(): UseMutationResult<LeaveRequest, unknown, string> {
  const client = useApiClient();
  const invalidate = useAttendanceInvalidation();
  return useMutation({
    mutationFn: (requestId: string) => cancelLeaveRequest(client, requestId),
    onSuccess: invalidate,
  });
}
